import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React, {useEffect, useState} from 'react';

import {SampleStatusItem} from 'constants/SampleStatusConstants';
import StorableChip from 'inventory/chips/StorableChip';
import {SampleControlsFeatureLevel} from 'inventory/sample-status/SampleControlsFeatureLevel';
import {hasMinimumSCRFeatureLevel} from 'inventory/sample-status/SampleControlsFeatureLevelUtil';
import SampleStatusLabel from 'inventory/sample-status/SampleStatusLabel';
import {
  getCICOStatusText,
  getRestrictionAccessDisplayText,
  getRestrictionAccessHeaderText,
  getSampleOwnersText,
  queryForItemSampleSupport,
} from 'inventory/sample-status/SampleStatusPresentationUtils';
import {TABLE_HEADER_HEIGHT} from 'notebook/tables/constants/TableLayoutConstants';
import Tooltip from 'ui-library/bs/Tooltip';
import {ChipSize} from 'ui-library/chips/Chip';
import {
  COMPACT_TABLE_PADDING_VERTICAL,
  HORIZONTAL_SCROLLBAR_HEIGHT,
  RowSelectionMode,
  ScrollableTableColumn,
  ScrollableTableRow,
  TABLE_BODY_BORDER_SIZE,
  TABLE_KEYS,
  TABLE_ROW_BODY_HEIGHT,
} from 'ui-library/scrollable/ScrollableTableConstants';
import SortableScrollableTable from 'ui-library/scrollable/SortableScrollableTable';

export interface SampleStatusTableProps {
  setSelectedSampleIds(newSelectedSampleIds: Array<string>): void;
  selectedSampleIds: Array<string>;
  samples: Array<SampleStatusItem>;
  allowSelection: boolean;
  canSelectMultiple?: boolean; // Defaults to true
}

interface SampleStatusRowData {
  name: JSX.Element;
  sampleOwners: JSX.Element | string;
  availability: JSX.Element | string;
  authorization: JSX.Element | string;
}

interface SampleStatusRow extends ScrollableTableRow {
  data: SampleStatusRowData;
  extra: {sample: SampleStatusItem};
}

type SampleStatusRowKey = keyof SampleStatusRowData;

export default function SampleStatusTable(props: SampleStatusTableProps): JSX.Element {
  const [sampleOwnersTexts, setSampleOwnersTexts] = useState<Map<string, string> | null>(null);
  const [CICOStatusTexts, setCICOStatusTexts] = useState<Map<string, string> | null>(null);
  const [supportsSampleStatus, setSupportsSampleStatus] = useState<Map<string, boolean> | null>(null);

  useEffect(() => {
    const updateSampleOwnersAndCICOStatus = (): void => {
      const supportsSampleStatusPromises: Array<Promise<boolean>> = props.samples.map((sample) =>
        queryForItemSampleSupport(sample.id)
      );

      Promise.all(supportsSampleStatusPromises).then((supportsSampleStatus) => {
        const newSampleOwners = new Map();
        const newCICOStatuses = new Map();
        const newSupportsSampleStatus = new Map();

        for (let i = 0; i < props.samples.length; i++) {
          newSampleOwners.set(
            props.samples[i].id,
            getSampleOwnersText(props.samples[i], supportsSampleStatus[i])
          );
          newCICOStatuses.set(
            props.samples[i].id,
            getCICOStatusText(props.samples[i], supportsSampleStatus[i], {hideAssignee: true})
          );
          newSupportsSampleStatus.set(props.samples[i].id, supportsSampleStatus[i]);
        }
        setCICOStatusTexts(newCICOStatuses);
        setSampleOwnersTexts(newSampleOwners);
        setSupportsSampleStatus(newSupportsSampleStatus);
      });
    };
    updateSampleOwnersAndCICOStatus();
  }, [props.samples]);

  const handleRowsSelect = (rows: Array<SampleStatusRow>, isSelected: boolean): void => {
    const modifiedSampleIds = rows.map((row) => row.id);
    let newCheckedSamplesIds;
    if (isSelected) {
      const sampleIdsToAdd = modifiedSampleIds.filter(
        (sampleId) => !props.selectedSampleIds.includes(sampleId)
      );
      newCheckedSamplesIds = props.selectedSampleIds.concat(sampleIdsToAdd);
    } else {
      newCheckedSamplesIds = props.selectedSampleIds.filter(
        (sampleId) => !modifiedSampleIds.includes(sampleId)
      );
    }
    props.setSelectedSampleIds(newCheckedSamplesIds);
  };

  const getSampleStatusSortValue = (row: SampleStatusRow, sortKey: SampleStatusRowKey): string => {
    switch (sortKey) {
      case 'name':
        return row.extra.sample.sortKey ?? '';
      case 'availability':
        return CICOStatusTexts == null ? '' : CICOStatusTexts.get(row.extra.sample.id)!;
      case 'authorization':
        return getRestrictionAccessDisplayText(row.extra.sample);
      case 'sampleOwners':
        return sampleOwnersTexts == null ? '' : sampleOwnersTexts.get(row.extra.sample.id)!;
      default:
        return row.data[sortKey];
    }
  };

  const handleRowClick = (row: SampleStatusRow): void => {
    if (!props.allowSelection) {
      return;
    }
    // If the sample is already selected, deselect it.
    if (props.selectedSampleIds.includes(row.id)) {
      props.setSelectedSampleIds(props.selectedSampleIds.filter((id) => id !== row.id));
    } else {
      props.setSelectedSampleIds(props.selectedSampleIds.concat(row.id));
    }
  };

  const getSampleOwnersWithTooltip = (sampleOwnersText: string): JSX.Element | string => {
    if (sampleOwnersText === 'None' || sampleOwnersText === 'N/A') {
      return sampleOwnersText;
    }
    return (
      <Tooltip title={sampleOwnersText}>
        <span className={css(styles.sampleOwnersList)}>{sampleOwnersText}</span>
      </Tooltip>
    );
  };

  const rows: Array<SampleStatusRow> = props.samples.map((sample, idx) => {
    const data = {
      name: (
        <div>
          <StorableChip id={sample.id} size={ChipSize.SMALL} />
        </div>
      ),
      sampleOwners:
        sampleOwnersTexts == null
          ? 'Loading...'
          : getSampleOwnersWithTooltip(sampleOwnersTexts.get(sample.id)!),
      availability:
        supportsSampleStatus == null ? (
          'Loading...'
        ) : (
          <div>
            <SampleStatusLabel
              item={sample}
              includeAuthorization={false}
              includeAvailability={true}
              hideAssigneeInLabel={true}
              renderAllStatuses={true}
              itemSupportsSampleStatus={supportsSampleStatus.get(sample.id)!}
            />
          </div>
        ),
      authorization:
        supportsSampleStatus == null ? (
          'Loading...'
        ) : (
          <div>
            <SampleStatusLabel
              item={sample}
              includeAuthorization={true}
              includeAvailability={false}
              renderAllStatuses={true}
              itemSupportsSampleStatus={supportsSampleStatus.get(sample.id)!}
            />
          </div>
        ),
    };
    return {
      id: sample.id,
      data,
      extra: {sample},
      isSelected: props.selectedSampleIds.includes(sample.id),
      rowOnlyClassName: classNames('sampleStatusWarning-row', {
        'sampleStatusWarning-lastRow': props.samples.length - 1 === idx,
      }),
    };
  });

  const shouldShowSCRColumns = hasMinimumSCRFeatureLevel(SampleControlsFeatureLevel.V1);
  const columns: Array<ScrollableTableColumn> = [
    {header: 'Name', accessorKey: 'name', isSortable: true, sortKey: 'name', width: 135, isFixed: true},
    {
      header: 'Availability',
      accessorKey: 'availability',
      isSortable: true,
      sortKey: 'availability',
      ...(shouldShowSCRColumns ? {width: 150} : {flexGrow: 1}),
    },
  ];
  if (shouldShowSCRColumns) {
    columns.push(
      {
        header: getRestrictionAccessHeaderText(),
        accessorKey: 'authorization',
        isSortable: true,
        sortKey: 'authorization',
        width: 150,
      },
      {
        header: 'Sample owners',
        accessorKey: 'sampleOwners',
        isSortable: true,
        sortKey: 'sampleOwners',
        flexGrow: 1,
      }
    );
  }

  const rowHeight = TABLE_ROW_BODY_HEIGHT + COMPACT_TABLE_PADDING_VERTICAL * 2 + TABLE_BODY_BORDER_SIZE * 2;
  const tableHeight = rowHeight * rows.length + TABLE_HEADER_HEIGHT + HORIZONTAL_SCROLLBAR_HEIGHT;
  const tableStyle = StyleSheet.create({
    sampleStatusTable: {
      height: tableHeight,
      maxHeight: '70vh',
    },
  });

  return (
    <SortableScrollableTable
      canResizeColumns={true}
      isCompact={true}
      rowSelectionMode={props.canSelectMultiple !== false ? RowSelectionMode.MULTIPLE : null}
      disableSelection={!props.allowSelection}
      columns={columns}
      className={classNames('sampleStatusWarning', css(tableStyle.sampleStatusTable))}
      initialRows={rows}
      onRowClick={handleRowClick}
      onRowsSelect={handleRowsSelect}
      initialSort={[]}
      getSortValue={getSampleStatusSortValue}
      tableKey={TABLE_KEYS.SAMPLE_STATUS_TABLE}
      matchParentHeight={true}
    />
  );
}

const styles = StyleSheet.create({
  sampleOwnersList: {
    display: 'inline-block',
    width: '100%',
  },
});
